import React, { useState, useEffect, useRef } from "react";
import { Link, Element } from "react-scroll";
import emailjs from "@emailjs/browser";
import "../Styling/Home.css";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Pagination, Navigation, Autoplay } from "swiper/modules";

function Home() {
  // Mobile Menu State
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [formMessage, setFormMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useRef();
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    message: "",
  });

  // Add state for modal
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);

  // Add refs for sections
  const sectionRefs = useRef([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Initialize the observer with proper options
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add a small delay to ensure smooth animation
            setTimeout(() => {
              entry.target.classList.add('visible');
            }, 50);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -10% 0px'
      }
    );

    const sections = sectionRefs.current;
    sections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  // Add TikTok embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.tiktok.com/embed.js';
    
    // Only add if not already present
    if (!document.querySelector('script[src="https://www.tiktok.com/embed.js"]')) {
      document.body.appendChild(script);
    }

    return () => {
      // Clean up
      const existingScript = document.querySelector('script[src="https://www.tiktok.com/embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();

    const emailParams = {
      user_name: formData.user_name,
      user_email: formData.user_email,
      message: formData.message,
    };

    emailjs.send(
      "default_service",

      "template_7ku75jv",
      emailParams,
      "GKsbndyN5iMz9Csyb"
    )
    .then(() => {
      setFormMessage("Message sent successfully!");
      setIsSuccess(true);
      setFormData({ user_name: "", user_email: "", message: "" });
    })
    .catch((error) => {
      setFormMessage("Error sending message. Please try again.");
      setIsSuccess(false);
      console.error("EmailJS Error:", error);
    });
  };

  // Add modal component
  const TestimonialModal = ({ testimonial, onClose }) => {
    useEffect(() => {
      const handleEsc = (e) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    if (!testimonial) return null;

    return (
      <div 
        className={`testimonial-modal-overlay ${testimonial ? 'active' : ''}`}
        onClick={onClose}
      >
        <div 
          className={`testimonial-modal ${testimonial ? 'active' : ''}`}
          onClick={e => e.stopPropagation()}
        >
          <button className="modal-close-btn" onClick={onClose}>×</button>
          <img 
            src={testimonial.image} 
            alt={testimonial.name} 
            className="modal-image"
          />
          <div className="modal-content">
            <p className="modal-text">{testimonial.text}</p>
            <h3 className="modal-author">{testimonial.name}</h3>
            <p className="modal-role">{testimonial.role}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="home">
      {/* Header */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: isMobile ? "10px 15px" : "7px 30px",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 10,
          boxShadow: "0px 4px 15px rgba(212, 175, 55, 0.3)",
          borderBottom: "1px solid rgba(212, 175, 55, 0.5)",
          backdropFilter: "blur(10px)"
        }}
      >
        <div
          style={{
            flex: "1",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            marginLeft: isMobile ? "10px" : "20px",
          }}
        >
          <img
            src="/Transparent.png"
            alt="Valeno Fit Logo"
            style={{
              height: isMobile ? "50px" : "70px",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </div>

        {isMobile && (
          <div
            className="hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              display: "block",
              fontSize: "2rem",
              color: menuOpen ? "#D4AF37" : "white",
              cursor: "pointer",
              padding: "10px",
              transition: "color 0.3s ease",
              outline: "none",
              userSelect: "none",
              WebkitTapHighlightColor: "transparent"
            }}
          >
            ☰
          </div>
        )}

        <nav
          className={`navbar ${menuOpen ? "active" : ""}`}
          style={{
            flex: "2",
            display: isMobile ? (menuOpen ? "flex" : "none") : "flex",
            justifyContent: isMobile ? "center" : "flex-start",
            flexDirection: isMobile ? "column" : "row",
            backgroundColor: isMobile ? "rgba(0, 0, 0, 0.95)" : "transparent",
            position: isMobile ? "absolute" : "static",
            top: isMobile ? "60px" : "auto",
            right: isMobile ? "0" : "auto",
            width: isMobile ? "100%" : "auto",
            padding: isMobile ? "15px 0" : "0",
            paddingRight: isMobile ? "0" : "300px",
          }}
        >
          <ul
            style={{
              listStyle: "none",
              display: "flex",
              gap: isMobile ? "15px" : "20px",
              margin: 0,
              padding: 0,
              alignItems: "center",
              flexDirection: isMobile ? "column" : "row",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            <li>
              <Link
                to="home"
                smooth={true}
                duration={500}
                style={{
                  textDecoration: "none",
                  color: "white",
                  fontWeight: "bold",
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s ease",
                  display: "block",
                  textAlign: "center",
                  position: "relative",
                  "&:hover": {
                    color: "#D4AF37",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="intro-container"
                smooth={true}
                duration={500}
                offset={-200}
                style={{
                  textDecoration: "none",
                  color: "white",
                  fontWeight: "bold",
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s ease",
                  display: "block",
                  textAlign: "center",
                  position: "relative",
                  "&:hover": {
                    color: "#D4AF37",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Mission
              </Link>
            </li>
            <li>
              <Link
                to="testimonials"
                smooth={true}
                duration={500}
                style={{
                  textDecoration: "none",
                  color: "white",
                  fontWeight: "bold",
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s ease",
                  display: "block",
                  textAlign: "center",
                  position: "relative",
                  "&:hover": {
                    color: "#D4AF37",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Transformations
              </Link>
            </li>
            <li>
              <Link
                to="meet-trainer"
                smooth={true}
                duration={500}
                style={{
                  textDecoration: "none",
                  color: "white",
                  fontWeight: "bold",
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s ease",
                  display: "block",
                  textAlign: "center",
                  position: "relative",
                  "&:hover": {
                    color: "#D4AF37",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                About 
              </Link>
            </li>
            <li>
              <Link
                to="social-media-section"
                smooth={true}
                duration={500}
                style={{
                  textDecoration: "none",
                  color: "white",
                  fontWeight: "bold",
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s ease",
                  display: "block",
                  textAlign: "center",
                  position: "relative",
                  "&:hover": {
                    color: "#D4AF37",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Socials
              </Link>
            </li>
            <li>
              <Link
                to="contact"
                smooth={true}
                duration={500}
                offset={20}
                style={{
                  textDecoration: "none",
                  color: "white",
                  fontWeight: "bold",
                  padding: "10px 15px",
                  borderRadius: "5px",
                  transition: "all 0.3s ease",
                  display: "block",
                  textAlign: "center",
                  position: "relative",
                  "&:hover": {
                    color: "#D4AF37",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                Contact
              </Link>
            </li>
          </ul>
        </nav>
      </header>

    {/* Hero Section */}
<Element name="home" className="hero">
  <div
    className="hero-bg"
    style={{
      backgroundImage: "linear-gradient(to right, #404040, #0a0a0a)",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.9,
      maxHeight: "95vh"
    }}
  ></div>

  <div
    className="hero-content"
    style={{
      position: "relative",
      zIndex: 2,
      display: "flex",
      width: "100%",
      height: "80vh",
      maxWidth: "1400px",
      margin: "0 auto",
      padding: "0 clamp(40px, 8vw, 120px)",
      alignItems: "center",
      justifyContent: "space-between"
    }}
  >
    <div
      style={{
        flex: "1",
        maxWidth: "800px",
        display: "flex",
        flexDirection: "column",
        gap: "clamp(30px, 5vh, 60px)",
        transform: "translateY(-20vh)" // You can reduce or remove this if you want it higher
      }}
    >
      <h1
        style={{
          fontSize: "clamp(4.5rem, 8vw, 8.5rem)",
          fontWeight: "300",
          lineHeight: "1",
          color: "white",
          margin: "0",
          fontFamily: "'Inter', sans-serif",
          letterSpacing: "-0.02em"
        }}
      >
        Valeno Fit
      </h1>
      <p
        style={{
          fontSize: "clamp(1.1rem, 1.8vw, 1.4rem)",
          color: "#ffffff",
          opacity: "0.9",
          margin: "0",
          maxWidth: "500px",
          letterSpacing: "0.02em"
        }}
      >
        With our Advanced Training Techniques
      </p>
      <a
        href="https://docs.google.com/forms/d/e/1FAIpQLSd2lHPEe1qbm9zsLUb3NFyfISPo9ZY7CkdQENQu-GdS1sFMKA/viewform"
        target="_blank"
        rel="noopener noreferrer"
        className="join-now-btn no-underline"
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "clamp(14px, 1.8vw, 20px) clamp(28px, 2.5vw, 40px)",
          fontSize: "clamp(1rem, 1.4vw, 1.2rem)",
          color: "white",
          backgroundColor: "transparent",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          borderRadius: "4px",
          textDecoration: "none",
          transition: "all 0.3s ease",
          width: "fit-content",
          marginTop: "clamp(10px, 2vh, 20px)",
          letterSpacing: "0.03em"
        }}
      >
        Take Action Now{" "}
        <span
          style={{
            marginLeft: "12px",
            fontSize: "1.2em",
            transition: "transform 0.3s ease"
          }}
        >
          →
        </span>
      </a>
    </div>
    <div
      style={{
        flex: 1,
        maxWidth: "40%",
        height: "85vh",
        position: "relative",
        overflow: "hidden",
        borderRadius: "20px",
        margin: "0",
        marginRight: "-150px",
        marginBottom: "100px"
      }}
    >
      <img
        src="/heroImage.jpeg"
        alt="Valeno Fit"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          filter: "grayscale(100%)",
          borderRadius: "20px",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.3)"
        }}
      />
    </div>
  </div>
</Element>


      {/* Our Mission Section */}
      <Element name="intro-container">
        <div 
          ref={el => sectionRefs.current[0] = el}
          className="section-card section-animate" 
          style={{
            marginTop: "-60px",
            position: "relative",
            zIndex: 1
          }}
        >
          <div style={{
            padding: "120px 40px",
            maxWidth: "1200px",
            margin: "0 auto",
            position: "relative",
          }}>
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
              <span style={{
                color: "#D4AF37",
                fontSize: "1rem",
                letterSpacing: "3px",
                textTransform: "uppercase",
                marginBottom: "30px"
              }}>
                Our Mission
              </span>

              <h2 style={{
                color: "white",
                fontSize: "clamp(2.5rem, 4vw, 3.5rem)",
                lineHeight: "1.2",
                fontWeight: "500",
                maxWidth: "800px",
                margin: "0 0 40px 0"
              }}>
                Transform Your Body,{" "}
                <span style={{ color: "#D4AF37" }}>
                  Transform Your Life
                </span>
              </h2>

              <p style={{
                color: "#8892b0",
                fontSize: "clamp(1rem, 1.2vw, 1.1rem)",
                lineHeight: "1.8",
                maxWidth: "600px",
                margin: "0 auto 50px"
              }}>
                At Valeno Fit, we're dedicated to delivering exceptional results through expert coaching, personalized training programs, and comprehensive nutrition guidance.
              </p>

              <div style={{
                display: "flex",
                gap: "40px",
                justifyContent: "center",
                marginTop: "20px"
              }}>
                <div style={{
                  textAlign: "center"
                }}>
                  <h3 style={{
                    color: "#D4AF37",
                    fontSize: "clamp(2rem, 3vw, 2.5rem)",
                    margin: "0 0 10px 0"
                  }}>5+</h3>
                  <p style={{
                    color: "#8892b0",
                    fontSize: "0.9rem",
                    margin: "0"
                  }}>Years Experience</p>
                </div>
                <div style={{
                  width: "1px",
                  background: "rgba(212, 175, 55, 0.3)"
                }}></div>
                <div style={{
                  textAlign: "center"
                }}>
                  <h3 style={{
                    color: "#D4AF37",
                    fontSize: "clamp(2rem, 3vw, 2.5rem)",
                    margin: "0 0 10px 0"
                  }}>20+</h3>
                  <p style={{
                    color: "#8892b0",
                    fontSize: "0.9rem",
                    margin: "0"
                  }}>Clients Transformed</p>
                </div>
              </div>
            </div>

            <div style={{
              position: "absolute",
              bottom: 0,
              left: "40px",
              right: "40px",
              height: "1px",
              background: "linear-gradient(to right, rgba(212, 175, 55, 0.1), rgba(212, 175, 55, 0.3), rgba(212, 175, 55, 0.1))"
            }}></div>
          </div>
        </div>
      </Element>

      {/* Testimonials Section */}
      <Element name="testimonials">
        <div 
          ref={el => sectionRefs.current[1] = el}
          className="section-card section-animate"
        >
          <div className="testimonials">
            <h2 className="section-title"><span>Transformations</span></h2>
            <div className="testimonials-container">
              <Swiper
                modules={[Pagination, Navigation, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                  },
                  1024: {
                    slidesPerView: 3,
                  },
                }}
                className="testimonial-carousel"
              >
                <SwiperSlide>
                  <div 
                    className="testimonial-card"
                    onClick={() => setSelectedTestimonial({
                      image: "/test/seb.jpeg",
                      name: "Seb",
                      text: "I'd always been pretty lean and wanted to put on more muscle. Misha made it super easy to follow everything, and after 6 months, I was exactly where I wanted to be",
                      role: "Client"
                    })}
                  >
                    <img
                      src="/test/seb.jpeg"
                      alt="Seb's Transformation"
                      className="testimonial-image"
                    />
                    <p className="testimonial-text">
                      "I'd always been pretty lean and wanted to put on more muscle. Misha made it super easy to follow everything, and after 6 months, I was exactly where I wanted to be"
                    </p>
                    <h3 className="testimonial-author">Seb</h3>
                  </div>
                </SwiperSlide>

                <SwiperSlide>
                  <div 
                    className="testimonial-card"
                    onClick={() => setSelectedTestimonial({
                      image: "/test/misha.jpeg",
                      name: "Misha",
                      text: "As a trainer, I've dedicated myself to helping others achieve their fitness goals. My own transformation journey has given me the experience and knowledge to guide others effectively.",
                      role: "Trainer"
                    })}
                  >
                    <img
                      src="/test/misha.jpeg"
                      alt="Misha's Transformation"
                      className="testimonial-image"
                    />
                    <p className="testimonial-text">
                      "As a trainer, I've dedicated myself to helping others achieve their fitness goals. My own transformation journey has given me the experience and knowledge to guide others effectively."
                    </p>
                    <h3 className="testimonial-author">Misha</h3>
                  </div>
                </SwiperSlide>

                <SwiperSlide>
                  <div 
                    className="testimonial-card"
                    onClick={() => setSelectedTestimonial({
                      image: "/test/logan.jpeg",
                      name: "Logan",
                      text: "First-team football was taking over, and I honestly didn't think I could keep up with the gym. But Misha made a plan that worked around the tiredness from training, and I still made solid progress.",
                      role: "Client"
                    })}
                  >
                    <img
                      src="/test/logan.jpeg"
                      alt="Logan's Transformation"
                      className="testimonial-image"
                    />
                    <p className="testimonial-text">
                      "First-team football was taking over, and I honestly didn't think I could keep up with the gym. But Misha made a plan that worked around the tiredness from training, and I still made solid progress."
                    </p>
                    <h3 className="testimonial-author">Logan</h3>
                  </div>
                </SwiperSlide>

                <SwiperSlide>
                  <div 
                    className="testimonial-card"
                    onClick={() => setSelectedTestimonial({
                      image: "/test/cian.jpeg",
                      name: "Cian",
                      text: "I didn't expect to see such a big difference, but Misha's training and nutrition advice made it really easy to stay consistent. I made more progress than I ever thought I would.",
                      role: "Client"
                    })}
                  >
                    <img
                      src="/test/cian.jpeg"
                      alt="Cian's Transformation"
                      className="testimonial-image"
                    />
                    <p className="testimonial-text">
                      "I didn't expect to see such a big difference, but Misha's training and nutrition advice made it really easy to stay consistent. I made more progress than I ever thought I would."
                    </p>
                    <h3 className="testimonial-author">Cian</h3>
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>
          </div>
        </div>
      </Element>

      {/* Meet Your Trainer Section */}
      <Element name="meet-trainer">
        <div 
          ref={el => sectionRefs.current[2] = el}
          className="section-card section-animate"
        >
          <div className="meet-trainer" style={{ height: "80vh" }}>
            <div className="trainer-content">
              <h2 className="trainer-title"><span>Meet Your Trainer</span></h2>
              <h3 className="trainer-name">Misha Larin</h3>
              <p className="trainer-description">
At 17 years old, I placed 4th in the UK Natural Bodybuilding Finals through self-coaching and disciplined training. With over five years of gym experience, I've helped more than 20 clients transform their bodies and build lasting confidence.              </p>
              <p className="trainer-quote">
              "What we've created isn't just a fitness program. It's a system built on the insights of elite coaches and refined through hands-on experience. Progress isn't a finish line — it's our standard."              
              </p>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSd2lHPEe1qbm9zsLUb3NFyfISPo9ZY7CkdQENQu-GdS1sFMKA/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="get-started-btn"
                style={{ 
                  display: "inline-block", 
                  marginTop: "20px",
                  fontSize: "1.2rem",
                  padding: "15px 30px",
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  textAlign: "center",
                  backgroundColor: "#D4AF37",
                  color: "black",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  textDecoration: "none"
                }}
              >
                Get Started
              </a>
            </div>
            <div className="trainer-image">
              <img src="/trainer.png" alt="Misha Iarin - Personal Trainer" />
            </div>
          </div>
        </div>
      </Element>

      <Element name="social-media-section">
  <div
    ref={(el) => (sectionRefs.current[3] = el)}
    className="section-card section-animate"
  >
    <div className="social-media-section">
      <h2
        className="social-title"
      >
        Socials
      </h2>

      <div
        className="social-media-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
          gap: "200px",
          flexWrap: "wrap",
          padding: "0 20px",
          maxWidth: "1200px",
          margin: "0 auto"
        }}
      >
        {/* Instagram Embed */}
        <div style={{ 
          flex: "0 1 auto",
          minWidth: "325px",
          maxWidth: "400px",
          height: "500px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}>
          <iframe
            title="Misha Larin on Instagram"
            src="https://www.instagram.com/mish.himself/embed/"
            className="instagram-frame"
            allowTransparency="true"
            frameBorder="0"
            scrolling="no"
            allowFullScreen
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "12px",
              border: "1px solid rgba(212, 175, 55, 0.5)",
              boxShadow: "0 4px 20px rgba(212, 175, 55, 0.2), 0 0 30px rgba(212, 175, 55, 0.3), 0 0 50px rgba(212, 175, 55, 0.1)",
            }}
          ></iframe>
        </div>

        {/* TikTok Embed */}
        <div style={{ 
          flex: "0 1 auto",
          minWidth: "325px",
          maxWidth: "400px",
          height: "500px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "white",
          borderRadius: "12px",
          border: "1px solid rgba(212, 175, 55, 0.5)",
          boxShadow: "0 4px 20px rgba(212, 175, 55, 0.2), 0 0 30px rgba(212, 175, 55, 0.3), 0 0 50px rgba(212, 175, 55, 0.1)"
        }}>
          <blockquote 
            className="tiktok-embed" 
            cite="https://www.tiktok.com/@misha_larin/video/7276567384746495264" 
            data-video-id="7276567384746495264" 
            data-embed-from="oembed" 
            style={{
              maxWidth: "605px",
              minWidth: "325px",
              width: "100%",
              height: "100%",
              margin: "0",
              padding: "0",
              borderRadius: "12px"
            }}
          > 
            <section> 
              <a 
                target="_blank" 
                title="@misha_larin" 
                href="https://www.tiktok.com/@misha_larin?refer=embed"
                rel="noreferrer"
              >
                @misha_larin
              </a> 
            </section> 
          </blockquote>
        </div>
      </div>
    </div>
  </div>
</Element>


      {/* Contact Section */}
      <Element name="contact">
        <div 
          ref={el => sectionRefs.current[4] = el}
          className="section-card section-animate"
        >
          <div className="contact">
            <div className="contact-container">
              <h2 className="contact-title">Get in Touch</h2>
              <p className="contact-description">
                Got questions? We'd love to hear from you. Connect with us today!
              </p>
              <div className="contact-grid">
                <form ref={form} className="contact-form" onSubmit={sendEmail}>
                  <h3>Send Us a Message</h3>
                  <input 
                    type="text" 
                    name="user_name"
                    placeholder="Your Name" 
                    required 
                    value={formData.user_name}
                    onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                  />
                  <input 
                    type="email" 
                    name="user_email"
                    placeholder="Your Email" 
                    required 
                    value={formData.user_email}
                    onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                  />
                  <textarea 
                    name="message"
                    placeholder="Your Message" 
                    rows="5" 
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  ></textarea>
                  <button type="submit" style={{ backgroundColor: "#D4AF37", color: "black" }}>Submit</button>

                  {formMessage && (
                    <p style={{ 
                      color: isSuccess ? "green" : "red", 
                      fontWeight: "bold", 
                      marginTop: "10px" 
                    }}>
                      {formMessage}
                    </p>
                  )}
                </form>
                
                <div className="contact-info">
                  <h3>Contact Details</h3>
                  <p><strong>Email:</strong> valenofit@gmail.com</p>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </Element>

      {/* Footer */}
      <footer className="footer">
        <p>Website Powered By: advancedtechs.ca </p>
        <p>&copy; 2025 Valeno Fit. All Rights Reserved.</p>
      </footer>

      {/* Add modal to the render */}
      {selectedTestimonial && (
        <TestimonialModal
          testimonial={selectedTestimonial}
          onClose={() => setSelectedTestimonial(null)}
        />
      )}
    </div>
  );
}

export default Home;